// Value Objects with validation

import { smartRoundPrice } from '../utils/currency';

export class Email {
  private readonly value: string;

  constructor(email: string) {
    if (!Email.isValid(email)) {
      throw new Error('Invalid email address');
    }
    this.value = email.toLowerCase();
  }

  static isValid(email: string): boolean {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  }

  toString(): string {
    return this.value;
  }

  equals(other: Email): boolean {
    return this.value === other.value;
  }
}

export class Age {
  private readonly value: number;

  constructor(age: number) {
    if (age < 0 || age > 120) {
      throw new Error('Invalid age');
    }
    this.value = age;
  }

  getRange(): string {
    if (this.value < 20) return 'under-20';
    if (this.value < 30) return '20-29';
    if (this.value < 40) return '30-39';
    if (this.value < 50) return '40-49';
    return '50-plus';
  }

  getValue(): number {
    return this.value;
  }
}

export class VoucherCode {
  private readonly value: string;

  constructor() {
    const now = new Date();
    const monthNames = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
                        'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const month = monthNames[now.getMonth()];
    const year = now.getFullYear();
    this.value = `${month}${year}GLOW`;
  }

  toString(): string {
    return this.value;
  }
}

export class Price {
  private readonly amount: number;
  private readonly currency: string;

  constructor(amount: number, currency: string = 'USD') {
    if (amount < 0) {
      throw new Error('Price cannot be negative');
    }
    this.amount = amount;
    this.currency = currency;
  }

  getAmount(): number {
    return this.amount;
  }

  getCurrency(): string {
    return this.currency;
  }

  getMonthlyFromAnnual(): Price {
    return new Price(this.amount / 12, this.currency);
  }

  format(): string {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency
    });
    return formatter.format(this.amount);
  }

  applyDiscount(percentage: number): Price {
    const discountedAmount = this.amount * (1 - percentage / 100);
    return new Price(smartRoundPrice(this.amount, discountedAmount), this.currency);
  }
}

export class Percentage {
  private readonly value: number;

  constructor(value: number) {
    if (value < 0 || value > 100) {
      throw new Error('Percentage must be between 0 and 100');
    }
    this.value = value;
  }

  getValue(): number {
    return this.value;
  }

  toString(): string {
    return `${this.value}%`;
  }

  toDecimal(): number {
    return this.value / 100;
  }
}

export class SkinScore {
  private readonly value: number;

  constructor(value: number) {
    if (value < 0 || value > 100) {
      throw new Error('Skin score must be between 0 and 100');
    }
    this.value = Math.round(value);
  }

  getValue(): number {
    return this.value;
  }

  getLevel(): 'poor' | 'fair' | 'good' | 'excellent' {
    if (this.value < 40) return 'poor';
    if (this.value < 60) return 'fair';
    if (this.value < 80) return 'good';
    return 'excellent';
  }

  getColor(): string {
    if (this.value < 40) return '#F44336'; // red
    if (this.value < 70) return '#FF9800'; // orange
    return '#4CAF50'; // green
  }

  toString(): string {
    return `${this.value}%`;
  }
}

export class Timer {
  private endTime: Date;

  constructor(durationInSeconds: number) {
    this.endTime = new Date(Date.now() + durationInSeconds * 1000);
  }

  getRemainingSeconds(): number {
    const now = Date.now();
    const remaining = Math.max(0, this.endTime.getTime() - now);
    return Math.floor(remaining / 1000);
  }

  getRemainingTime(): { minutes: number; seconds: number } {
    const totalSeconds = this.getRemainingSeconds();
    return {
      minutes: Math.floor(totalSeconds / 60),
      seconds: totalSeconds % 60
    };
  }

  isExpired(): boolean {
    return this.getRemainingSeconds() === 0;
  }

  format(): string {
    const { minutes, seconds } = this.getRemainingTime();
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
}

export class UserId {
  private readonly value: string;

  constructor(value?: string) {
    if (value) {
      this.value = value;
    } else {
      this.value = this.generate();
    }
  }

  private generate(): string {
    return 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  toString(): string {
    return this.value;
  }

  equals(other: UserId): boolean {
    return this.value === other.value;
  }
}

export class SessionId {
  private readonly value: string;
  private readonly createdAt: Date;

  constructor() {
    this.value = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    this.createdAt = new Date();
  }

  toString(): string {
    return this.value;
  }

  getAge(): number {
    return Date.now() - this.createdAt.getTime();
  }

  isExpired(maxAgeMs: number = 30 * 60 * 1000): boolean {
    return this.getAge() > maxAgeMs;
  }
}

export class ImageData {
  private readonly data: string;
  private readonly mimeType: string;

  constructor(dataUrl: string) {
    if (!dataUrl.startsWith('data:image/')) {
      throw new Error('Invalid image data URL');
    }

    const matches = dataUrl.match(/^data:([^;]+);base64,(.+)$/);
    if (!matches) {
      throw new Error('Invalid base64 image data');
    }

    this.mimeType = matches[1];
    this.data = matches[2];
  }

  getMimeType(): string {
    return this.mimeType;
  }

  getBase64(): string {
    return this.data;
  }

  getDataUrl(): string {
    return `data:${this.mimeType};base64,${this.data}`;
  }

  getSize(): number {
    // Rough estimate of size in bytes
    return Math.round(this.data.length * 0.75);
  }
}